import './addRole.scss'
import { useEffect, useState, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'

import * as Yup from 'yup'
import { ErrorMessage, Field, Form, Formik } from 'formik'

import Button from '../../components/button/Button'
import NewInput from '../../components/input/NewInput'
import BreadCrumbs from '../../components/breadcrumb/Breadcrumb'

import { ROUTES_PATH, UI_CONSTANT } from '../../utility/constants'
import { handleName } from '../../utility/genricFunction'
import {
  addNewRoleWithPermissions,
  addPermissions,
  getallPermissions,
  getPermissionsForRole,
} from '../../store/builders/roleManagement/rolemanagement.builder.js'
import { ROLENAME_LENGTH, VALIDATION_MESSAGES } from '../../utility/constantValidationMessages.js'
import Loader from '../../components/loader/Loader.jsx'

const validationSchema = Yup.object().shape({
  roleName: Yup.string().required(VALIDATION_MESSAGES.NAME_REQUIRED).max(ROLENAME_LENGTH, VALIDATION_MESSAGES.ROLENAME_LENGTH_VALIDATION),
  isMobileAccess: Yup.string().required(VALIDATION_MESSAGES.PHONE_ACCESS_REQUIRED),
  isAdmin: Yup.string().required("Admin info is required"),
})

function AddNewRole() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { id } = useParams()
  const { allPermissions, roleData, permissionsFormRole } = useSelector((store) => store.roleManagement)
  const {  loading } = useSelector((state) => state?.reports)

  const [initialValues, setInitialValues] = useState({
    roleName: '',
    isMobileAccess: '',
    isAdmin:'',
    checkedPermissions: [],
    checkedParentPermissions: [],
  })


  useEffect(() => {
    dispatch(getallPermissions())
  }, [dispatch])

  useEffect(() => {
    if (id) {
      dispatch(getPermissionsForRole(id))
    }
  }, [id, dispatch])

  useEffect(() => {
      if(permissionsFormRole.length > 0){
      const role = permissionsFormRole && permissionsFormRole[0]
      const checkedPermissions = role.permissions
      const parentPermissions = []
      const isMobileAccess = permissionsFormRole[0].mobileAccess == 1 ? 'yes' : 'no'
      const isAdmin = permissionsFormRole[0].isAdmin == 1 ? 'yes' : 'no'

      allPermissions.forEach((permission) => {
        const hasCheckedChild = permission.permission_code.some((code) => checkedPermissions.includes(code))
        if (hasCheckedChild) {
          parentPermissions.push(permission.parent_permission_code)
        }
      })

      setInitialValues((prevValues) => ({
        ...prevValues,
        roleName: role.roleName,
        checkedPermissions: checkedPermissions,
        isMobileAccess: isMobileAccess,
        checkedParentPermissions: parentPermissions,
        isAdmin:isAdmin
      }))
    }
  }, [permissionsFormRole])

  const breadCrumbsArr = [
    {
      title: id ? UI_CONSTANT.ROLE_MANAGEMENT.EDIT_ROLE : UI_CONSTANT.ROLE_MANAGEMENT.ADD_ROLE,
      link: null,
    },
    {
      title: UI_CONSTANT.DASHBOARD,
      link: ROUTES_PATH.DASHBOARD,
    },
    {
      title: UI_CONSTANT.ROLE_MANAGEMENT.LABEL_ROLL_MANAGEMENT,
      link: ROUTES_PATH.ROLE_MANAGEMENT,
    },
    {
      title: id ? UI_CONSTANT.ROLE_MANAGEMENT.EDIT_ROLE : UI_CONSTANT.ROLE_MANAGEMENT.NEW_ROLE,
      link: id ? ROUTES_PATH.BRANCH_EDIT.replace(':id', id) : ROUTES_PATH.BRANCH_ADD,
    },
  ]
  function handleFormSubmit(values) {
    if (id) {
      dispatch(
        addPermissions({
          data: {
            roleId: id,
            permission: values.checkedPermissions,
            isMobileAccess: values.isMobileAccess == 'yes' ? true : false,
            isAdmin: values.isAdmin == 'yes' ? true : false,
          },
          navigate,
        }),
      )
    } else {
      dispatch(
        addNewRoleWithPermissions({
          data: {
            roleName: values.roleName,
            permissions: values.checkedPermissions,
            isMobileAccess: values.isMobileAccess == 'yes' ? true : false,
            isAdmin: values.isAdmin == 'yes' ? true : false,
          },
          navigate,
        }),
      )
    }
  }

  const handlePermissionChange = (e, values, setFieldValue, parentCode, permissionCodes) => {
    const { name, value, checked } = e.target

    let updatedPermissions = checked
      ? [...values.checkedPermissions, value]
      : values.checkedPermissions.filter((perm) => perm !== value)

    setFieldValue(name, updatedPermissions)

    const anyChildChecked = permissionCodes.some((code) => updatedPermissions.includes(code))
    if (anyChildChecked) {
      setFieldValue('checkedParentPermissions', [...new Set([...values.checkedParentPermissions, parentCode])])
    } else {
      const remainingParents = values.checkedParentPermissions.filter((parent) => parent !== parentCode)
      setFieldValue('checkedParentPermissions', remainingParents)
    }
  }

  const handleParentPermissionChange = (e, values, setFieldValue) => {
    const { value, checked } = e.target

    let updatedParentPermissions = checked
      ? [...values.checkedParentPermissions, value]
      : values.checkedParentPermissions.filter((perm) => perm !== value)

    setFieldValue('checkedParentPermissions', updatedParentPermissions)
  }

  return (
    <>
    {loading && <Loader loading={loading} />}
    {<Loader loading={loading} /> && <div className='addNewRole-wrapper'>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          handleFormSubmit(values)
        }}
        enableReinitialize
      >
        {({ values, handleSubmit, setFieldValue }) => (
          <Form>
            <div className='addNewRole-header'>
              <BreadCrumbs breadCrumbsArr={breadCrumbsArr} />

              <div className='addNewRole-header-right'>
                <Button customClassName={'save-btn'} onClick={handleSubmit}>
                  {UI_CONSTANT.SAVE}
                </Button>
              </div>
            </div>

            <div className='addNewRole'>
              <div className='addNewRole-inner-header'>
                <h1>{UI_CONSTANT.ROLE_MANAGEMENT.ROLE_INFORMATION}</h1>
                <p>{UI_CONSTANT.ROLE_MANAGEMENT.FORM_DESCRIPTION}</p>
              </div>

              <div className='separatorLine'></div>

              <div className='addNewRole-input-fields'>
                <NewInput
                  label='Role name'
                  placeholder='Enter role name'
                  name='roleName'
                  width={'100%'}
                  required={true}
                  onInput={handleName}
                  disabled={id ? true : false}
                />

                <div
                  role='group'
                  className='addNewRole-drawer-input-radio-options flex align-center mb-10'
                  aria-labelledby='title-radio-group'
                >
                  <p className='phone-access-label'>Mobile App access</p>
                  <label className='addNewRole-drawer-input-radio-option-label flex align-center'>
                    <Field
                      type='radio'
                      name='isMobileAccess'
                      value='yes'
                      className='custom-radio'
                      onChange={() => setFieldValue('isMobileAccess', 'yes')}
                      checked={values.isMobileAccess === 'yes'}
                    />
                    Yes
                  </label>
                  <label className='addNewRole-drawer-input-radio-option-label flex align-center'>
                    <Field
                      type='radio'
                      name='isMobileAccess'
                      value='no'
                      className='custom-radio'
                      onChange={() => setFieldValue('isMobileAccess', 'no')}
                      checked={values.isMobileAccess === 'no'}
                    />
                    No
                  </label>
                </div>
                <ErrorMessage name={"isMobileAccess"} component='div' className='error-msg' />
                
                <div
                  role='group'
                  className='addNewRole-drawer-input-radio-options flex align-center'
                  aria-labelledby='title-radio-group'
                >
                  <p className='phone-access-label'>Admin Access</p>
                  <label className='addNewRole-drawer-input-radio-option-label flex align-center'>
                    <Field
                      type='radio'
                      name='isAdmin'
                      // value='yes'
                      className='custom-radio'
                      onChange={() => setFieldValue('isAdmin', 'yes')}
                      checked={values.isAdmin === 'yes'}
                    />
                    Yes
                  </label>
                  <label className='addNewRole-drawer-input-radio-option-label flex align-center'>
                    <Field
                      type='radio'
                      name='isAdmin'
                      // value='no'
                      className='custom-radio'
                      onChange={() => setFieldValue('isAdmin', 'no')}
                      checked={values.isAdmin === 'no'}
                    />
                    No
                  </label>
                </div>
                <ErrorMessage name={"isAdmin"} component='div' className='error-msg' />

              </div>

              <div className='separatorLine'></div>

              <div className='allPermission-section-div'>
                <p className='allPermission-label'>{UI_CONSTANT.ROLE_MANAGEMENT.ALL_PERMISSIONS}</p>
                <div className='allPermissions-wrapper'>
                  {allPermissions?.map((permission, index) => (
                    <div className='permission-div-wrapper' key={index}>
                      <div className='permission-label'>
                        <Field
                          type='checkbox'
                          name='checkedParentPermissions'
                          value={permission.parent_permission_code}
                          checked={values.checkedParentPermissions.includes(permission.parent_permission_code)}
                          onChange={(e) => handleParentPermissionChange(e, values, setFieldValue)}
                        />
                        <p>{permission.parent_permission_name}</p>
                      </div>
                      <div className='permission-checkboxes'>
                        {permission?.permission_name?.map((per, idx) => (
                          <div className='permission-checkbox' key={idx}>
                            <Field
                              type='checkbox'
                              name='checkedPermissions'
                              value={permission.permission_code[idx]}
                              checked={values.checkedPermissions.includes(permission.permission_code[idx])}
                              onChange={(e) =>
                                handlePermissionChange(
                                  e,
                                  values,
                                  setFieldValue,
                                  permission.parent_permission_code,
                                  permission.permission_code,
                                )
                              }
                            />
                            <label>{per}</label>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>}
    </>
  )
}

export default AddNewRole
